import {
  AdditiveBlending,
  Color,
  DoubleSide,
  InstancedMesh,
  Matrix4,
  ShaderMaterial,
  SphereGeometry,
  Uniform,
  Vector3,
} from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

type BubbleSeed = {
  baseX: number;
  baseY: number;
  baseZ: number;
  size: number;
  speed: number;
  phase: number;
};

const BUBBLE_Y_RANGE = 18;
const BUBBLE_Y_MIN = -8;
const PLAYER_BUBBLE_CLEAR_RADIUS = 0.85;
const PLAYER_BUBBLE_CLEAR_RADIUS_SQ =
  PLAYER_BUBBLE_CLEAR_RADIUS * PLAYER_BUBBLE_CLEAR_RADIUS;
const PLAYER_BUBBLE_CLEAR_EPSILON_SQ = 0.000001;

const bubbleVertexShader = /* glsl */ `
  varying vec3 vNormalView;
  varying vec3 vViewDir;
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPosition = modelMatrix * instanceMatrix * vec4(position, 1.0);
    vec4 mvPosition = viewMatrix * worldPosition;

    vNormalView = normalize(normalMatrix * mat3(instanceMatrix) * normal);
    vViewDir = normalize(-mvPosition.xyz);
    vWorldPosition = worldPosition.xyz;

    gl_Position = projectionMatrix * mvPosition;
  }
`;

const bubbleFragmentShader = /* glsl */ `
  uniform vec3 bubbleColor;
  uniform float opacity;
  varying vec3 vNormalView;
  varying vec3 vViewDir;
  varying vec3 vWorldPosition;

  void main() {
    vec3 normal = normalize(vNormalView);
    vec3 viewDir = normalize(vViewDir);
    float rim = pow(1.0 - abs(dot(normal, viewDir)), 2.35);
    float topLight = pow(max(dot(normal, normalize(vec3(-0.35, 0.72, 0.58))), 0.0), 9.0);
    float lowerFade = smoothstep(-8.5, 6.5, vWorldPosition.y);
    float alpha = (rim * 1.78 + topLight * 1.55 + 0.035) * opacity * lowerFade;
    vec3 color = bubbleColor + vec3(topLight * 0.35);

    gl_FragColor = vec4(color, alpha);
  }
`;

export class Bubbles extends InstancedMesh {
  private readonly seeds: BubbleSeed[] = [];
  private readonly tmpMatrix = new Matrix4();
  private readonly tmpPosition = new Vector3();
  private readonly opacityUniform: Uniform<number>;
  private settings = DEFAULT_UNDERWATER_VISUAL_SETTINGS;

  constructor(roomSize: number, maxCount: number) {
    const geometry = new SphereGeometry(1, 24, 24);
    const bubbleColorUniform = new Uniform(new Color("#d8fbff"));
    const opacityUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.bubbleOpacity
    );
    const material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: AdditiveBlending,
      // side: DoubleSide,
      uniforms: {
        bubbleColor: bubbleColorUniform,
        opacity: opacityUniform,
      },
      vertexShader: bubbleVertexShader,
      fragmentShader: bubbleFragmentShader,
    });

    super(geometry, material, maxCount);

    this.opacityUniform = opacityUniform;
    this.frustumCulled = false;
    this.renderOrder = 12;
    this.count = Math.min(
      maxCount,
      Math.round(DEFAULT_UNDERWATER_VISUAL_SETTINGS.bubbleCount)
    );

    this.layers.set(1);

    for (let i = 0; i < maxCount; i += 1) {
      this.seeds.push({
        baseX: Math.random() * roomSize - roomSize / 2,
        baseY: Math.random() * BUBBLE_Y_RANGE + BUBBLE_Y_MIN,
        baseZ: Math.random() * roomSize - roomSize / 2,
        size: 0.018 + Math.random() * 0.012,
        speed: 2.55 + Math.random() * 0.95,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  setSettings(settings: UnderwaterVisualSettings) {
    this.settings = settings;
    this.count = Math.max(
      0,
      Math.min(this.seeds.length, Math.round(settings.bubbleCount))
    );
    this.visible = this.count > 0 && settings.bubbleOpacity > 0;
    this.opacityUniform.value = settings.bubbleOpacity;
  }

  update(time: number, playerPosition?: Vector3) {
    const {
      bubbleDrift,
      bubbleSize,
      bubbleSpeed,
      bubbleSpread,
    } = this.settings;
    const horizontalScale = Math.max(0.1, bubbleSpread);
    const speed = Math.max(0, bubbleSpeed);
    const sizeScale = Math.max(0.05, bubbleSize);

    for (let i = 0; i < this.count; i += 1) {
      const seed = this.seeds[i];
      const rise = (seed.baseY + time * speed * seed.speed + Math.abs(BUBBLE_Y_MIN)) % BUBBLE_Y_RANGE;
      const wave = time * 0.42 * speed + seed.phase;
      let x =
        seed.baseX * horizontalScale +
        Math.sin(wave) * 1.22 * bubbleDrift;
      const y = BUBBLE_Y_MIN + rise;
      let z =
        seed.baseZ * horizontalScale +
        Math.cos(wave * 1.83) * 0.22 * bubbleDrift;
      const scale = seed.size * sizeScale;

      if (playerPosition) {
        let offsetX = this.position.x + x - playerPosition.x;
        let offsetZ = this.position.z + z - playerPosition.z;
        let distanceSq = offsetX * offsetX + offsetZ * offsetZ;

        if (distanceSq < PLAYER_BUBBLE_CLEAR_RADIUS_SQ) {
          if (distanceSq < PLAYER_BUBBLE_CLEAR_EPSILON_SQ) {
            offsetX = Math.cos(seed.phase);
            offsetZ = Math.sin(seed.phase);
            distanceSq = 1;
          }

          const distance = Math.sqrt(distanceSq);
          const radiusScale = PLAYER_BUBBLE_CLEAR_RADIUS / distance;

          x = playerPosition.x + offsetX * radiusScale - this.position.x;
          z = playerPosition.z + offsetZ * radiusScale - this.position.z;
        }
      }

      this.tmpPosition.set(x, y, z);
      this.tmpMatrix.makeScale(scale, scale, scale);
      this.tmpMatrix.setPosition(this.tmpPosition);
      this.setMatrixAt(i, this.tmpMatrix);
    }

    this.instanceMatrix.needsUpdate = true;
  }
}
